import { RestResponseData } from '../../api/Rest';
import {
  getGameOpponents,
  RevisionGame,
  SyncGameRecordParams,
} from '../../api/Rest/record';
import { ToAttachmentWithPath } from '../../api/utils';
import { AIRevisionStatus } from '../../constants/MemoryGames/aiRevision';
// import { WebSocketTopicPushListener } from '../../api/WebSocket';
// import { WebSocketTopicType } from '../../api/WebSocket/push';
import { UserId, Uuid } from '../../models/Base';
import Repository, { Cursor, RepositoryJson } from '../../models/Repository';
import { AsyncActionOptions, FetchActionResult } from '../../models/Store';
import { RequireAtLeastOne } from '../../models/Utils';
import { SpaceSummary, SpaceSummaryJson } from '../SpaceStore';
import { WebBoosterType } from '../UserStore';

import { AlbumSummary, AlbumSummaryJson } from './AlbumSummary';
import { ChallengeRecord, ChallengeRecordJson } from './ChallengeRecord';
import { GameRecord, GameRecordJson } from './GameRecord';
import { RevisionSchedule, RevisionScheduleJson } from './RevisionSchedule';

export enum GameRecordListType {
  Rating = 'Rating',
}

export type GameRecordMapField = string;
export type GameRecordListField = string | GameRecordListType;
export class GameRecordRepository extends Repository<
  GameRecord,
  GameRecordJson<Date | number>,
  GameRecordMapField,
  GameRecordListField
> {
  constructor(
    repository?: RepositoryJson<
      GameRecordJson<Date | number>,
      GameRecordMapField,
      GameRecordListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new GameRecord(x));
  }
}

export type ChallengeRecordMapField = string;
export type ChallengeRecordListField = string;
export class ChallengeRecordRepository extends Repository<
  ChallengeRecord,
  ChallengeRecordJson<Date | number>,
  ChallengeRecordMapField,
  ChallengeRecordListField
> {
  constructor(
    repository?: RepositoryJson<
      ChallengeRecordJson<Date | number>,
      ChallengeRecordMapField,
      ChallengeRecordListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new ChallengeRecord(x));
  }
}

// AlbumSummaryRepository
export type AlbumSummaryMapField = string;
export type AlbumSummaryListField = string;

export enum AlbumSummaryMapType {
  Recent = 'Recent', // gameSetId_userId -> AlbumSummary
}

export class AlbumSummaryRepository extends Repository<
  AlbumSummary,
  AlbumSummaryJson<Date | number>,
  AlbumSummaryMapField,
  AlbumSummaryListField
> {
  constructor(
    repository?: RepositoryJson<
      AlbumSummaryJson<Date | number>,
      AlbumSummaryMapField,
      AlbumSummaryListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        mapFields: Repository.initFields(repository?.mapFields, {
          [AlbumSummaryMapType.Recent]: {},
        }),
      },
      (x) => new AlbumSummary(x),
    );
  }
}

// SpaceSummaryRepository
export type SpaceSummaryMapField = string;
export type SpaceSummaryListField = string;
export class SpaceSummaryRepository extends Repository<
  SpaceSummary,
  SpaceSummaryJson<Date | number>,
  SpaceSummaryMapField,
  SpaceSummaryListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceSummaryJson<Date | number>,
      SpaceSummaryMapField,
      SpaceSummaryListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new SpaceSummary(x));
  }
}

// RevisionScheduleRepository
export type RevisionScheduleMapField = string;
export type RevisionScheduleListField = string;
export class RevisionScheduleRepository extends Repository<
  RevisionSchedule,
  RevisionScheduleJson<Date | number>,
  RevisionScheduleMapField,
  RevisionScheduleListField
> {
  constructor(
    repository?: RepositoryJson<
      RevisionScheduleJson<Date | number>,
      RevisionScheduleMapField,
      RevisionScheduleListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new RevisionSchedule(x));
  }
}

export type PlayingRecord = RequireAtLeastOne<{
  gameRecord: ToAttachmentWithPath<GameRecordJson>;
  // slideshowRecord: SlideshowRecordJson;
  // challengeRecord: ChallengeRecordJson & { classroomId: Uuid };
}>;

export interface RecordStoreStateJson {
  // playingRecord: PlayingRecord | undefined;
  gameRecords: RepositoryJson<
    GameRecordJson,
    GameRecordMapField,
    GameRecordListField,
    number
  >;
  challengeRecords: RepositoryJson<
    ChallengeRecordJson,
    ChallengeRecordMapField,
    ChallengeRecordListField,
    number
  >;
  albumSummaries: RepositoryJson<
    AlbumSummaryJson,
    AlbumSummaryMapField,
    AlbumSummaryListField,
    number
  >;
  spaceSummaries: RepositoryJson<
    SpaceSummaryJson,
    SpaceSummaryMapField,
    SpaceSummaryListField,
    number
  >;
  revisionSchedules: RepositoryJson<
    RevisionScheduleJson,
    RevisionScheduleMapField,
    RevisionScheduleListField,
    number
  >;
}

export type RecordStoreState = {
  playingRecord: PlayingRecord | undefined;
  gameRecords: GameRecordRepository;
  challengeRecords: ChallengeRecordRepository;
  albumSummaries: AlbumSummaryRepository;
  spaceSummaries: SpaceSummaryRepository;
  revisionSchedules: RevisionScheduleRepository;
};

export interface RecordStoreAction {
  savePlayingRecord: (playingRecord: PlayingRecord) => void;
  syncPlayingRecord: () => Promise<void>;
  clearPlayingRecord: () => void;
  // // game record
  // addGameRecords: (
  //   classroomId: Uuid,
  //   gameSetId: Uuid | undefined,
  //   gameId: Uuid | undefined,
  //   ...gameRecords: GameRecord[]
  // ) => void;
  // setGameRecord: (gameRecord: GameRecord) => void;
  // setGameEvaluations: (...gameRecords: GameRecord[]) => void;
  getGameRecord: (gameRecordId: Uuid) => GameRecord | null | undefined;
  getGameRecords: (fieldName: string) => GameRecord[];
  // getGameRecordsBySpace: (spaceId: Uuid) => GameRecord[] | undefined;
  // getGameRecordsByAlbum: (gameSetId: Uuid) => GameRecord[] | undefined;
  // deleteGameRecords: (gameId: Uuid) => void;
  fetchGameRecords: (
    params: {
      fieldName: string;
      classroomId: Uuid;
      playedBy: UserId;
      gameSetId?: Uuid;
      gameId?: Uuid;
      from?: string;
      to?: string;
      hash?: string;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchGameRecordsByGameIds: (
    params: {
      fieldName: string;
      classroomId: Uuid;
      playedBy: UserId;
      // gameSetId?: Uuid;
      gameIds: Uuid[];
    },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  // fetchGameRecordsForEvaluation: (
  //   classroomId: Uuid,
  //   params: ModifiedPick<
  //     NonNullable<SecondParameter<typeof getGameRecords>>,
  //     'evaluated',
  //     'cursor' | 'limit'
  //   >,
  //   options?: AsyncActionOptions,
  // ) => Promise<FetchActionResult>;
  syncGameRecord: (gameRecord: SyncGameRecordParams) => Promise<{
    gameRecord: GameRecordJson;
    challengeRecord?: ChallengeRecordJson;
  }>;
  // syncGameEvaluation: (
  //   gameRecord: RequiredPick<
  //     ToAttachmentWithPath<GameRecordJson>,
  //     | 'classroomId'
  //     | 'gameRecordId'
  //     | 'reward'
  //     | 'score'
  //     | 'stars'
  //     | 'defeatedOpponents'
  //     | 'evaluation'
  //   >,
  // ) => Promise<void>;
  getChallengeRecord: (
    challengeRecordId: Uuid,
  ) => ChallengeRecord | null | undefined;
  getChallengeRecords: (fieldName: Uuid) => ChallengeRecord[];
  fetchChallengeRecords: (
    params: {
      fieldName: string;
      classroomId: Uuid;
      gameSetId: Uuid;
      hash?: string;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  createChallengeRecord: (
    challengeRecord: ChallengeRecordJson,
  ) => Promise<ChallengeRecordJson>;

  getAlbumSummary: (
    gameSetId: Uuid,
    userId: UserId,
  ) => AlbumSummary | null | undefined;
  clearAlbumSummary: (gameSetId: Uuid) => void;
  fetchAlbumSummaries: (
    params: { gameSetIds: Uuid[] },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  updateAlbumSummary: (classroomId: Uuid, gameSetId: Uuid) => Promise<void>;
  updateAlbumSummaryPath: (
    classroomId: Uuid,
    gameSetId: Uuid,
    body: { showGameForChallengeOnly?: boolean },
  ) => Promise<void>;
  moveInAlbumSummaryPath: (
    classroomId: Uuid,
    gameSetId: Uuid,
    moveTo: number,
  ) => Promise<void>;
  viewStartVideo: (classroomId: Uuid, gameSetId: Uuid) => Promise<void>;

  getSpaceSummary: (classroomId: Uuid) => SpaceSummary | null | undefined;
  fetchSpaceSummaries: (
    params: { classroomIds: Uuid[] },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  updateSpaceSummary: (classroomId: Uuid) => Promise<void>;
  // opponents
  fetchGameOpponents: (
    classroomId: Uuid,
    gameSetId: Uuid,
    gameId: Uuid,
    limit?: number,
  ) => Promise<
    RestResponseData<
      typeof getGameOpponents<{
        gameSetId: Uuid;
        gameId: Uuid;
        withProfiles: true;
      }>
    >
  >;
  getRevisionSchedule: (
    userId: Uuid,
    classroomId: string,
    gameId: string,
  ) => RevisionSchedule | null | undefined;
  getRevisionSchedules: (fieldName: Uuid) => RevisionSchedule[];
  fetchRevisionSchedules: (
    params: {
      classroomId: Uuid;
      gameSetId?: Uuid;
      userId: UserId;
      webBoosterType?: WebBoosterType | 'both';
      partitionId?: string;
      from?: string;
      to?: string;
    },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  fetchRevisionSchedulesByType: (
    params: {
      revisionType: AIRevisionStatus;
      classroomId: Uuid;
      gameSetId?: Uuid;
      userId: UserId;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  getRevisionScheduleGame: (
    params: {
      classroomId: string;
      gameSetId?: string;
      webBoosterType?: WebBoosterType | 'both';
      partitionId?: string;
      from?: string;
      to?: string;
    },
    options?: AsyncActionOptions,
  ) => Promise<RevisionGame | undefined>;
  // websocker listener
  // onUserTopicPush: WebSocketTopicPushListener<WebSocketTopicType.User>;
  // prune listeners
  onGameRecordPrune: (record: GameRecord) => void;
}
